import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, UserSearch, Briefcase, Award, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const avatars = [
  "https://images.unsplash.com/photo-1758613654360-45f1ff78c0cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMG5ldXRyYWwlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTc3NjcyMnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1758600587839-56ba05596c69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMG5ldXRyYWwlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTc3NjcyMnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1767175620484-1ed37931a0d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMG5ldXRyYWwlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTc3NjcyMnww&ixlib=rb-4.1.0&q=85",
];

const ExpertiseLocatorPage = () => {
  const { token, user } = useAuth();
  const [expertiseList, setExpertiseList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedExpertise, setSelectedExpertise] = useState(null);
  const [form, setForm] = useState({
    user_id: '', skills: '', qualifications: '', keywords: '', experience_years: 0, department: ''
  });

  useEffect(() => {
    fetchExpertise();
    fetchUsers();
  }, [search]);

  const fetchExpertise = async () => {
    try {
      const response = await axios.get(`${API}/expertise`, {
        params: { search },
        headers: { Authorization: `Bearer ${token}` },
      });
      setExpertiseList(response.data);
    } catch (error) {
      toast.error('Failed to fetch expertise');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleCreate = async () => {
    if (!form.user_id) {
      toast.error('Please select a user');
      return;
    }
    try {
      const payload = {
        ...form,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        qualifications: form.qualifications.split(',').map(s => s.trim()).filter(Boolean),
        keywords: form.keywords.split(',').map(s => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/expertise`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Expertise entry created');
      setIsCreateOpen(false);
      resetForm();
      fetchExpertise();
    } catch (error) {
      toast.error('Failed to create expertise');
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        qualifications: form.qualifications.split(',').map(s => s.trim()).filter(Boolean),
        keywords: form.keywords.split(',').map(s => s.trim()).filter(Boolean),
        experience_years: form.experience_years,
        department: form.department
      };
      await axios.put(`${API}/expertise/${selectedExpertise.expertise_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Expertise updated');
      setIsEditOpen(false);
      fetchExpertise();
    } catch (error) {
      toast.error('Failed to update expertise');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this expertise entry?')) return;
    try {
      await axios.delete(`${API}/expertise/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Expertise deleted');
      fetchExpertise();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (exp) => {
    setSelectedExpertise(exp);
    setForm({
      user_id: exp.user_id,
      skills: exp.skills?.join(', ') || '',
      qualifications: exp.qualifications?.join(', ') || '',
      keywords: exp.keywords?.join(', ') || '',
      experience_years: exp.experience_years || 0,
      department: exp.department || ''
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setForm({ user_id: '', skills: '', qualifications: '', keywords: '', experience_years: 0, department: '' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div data-testid="expertise-locator-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Expertise Locator</h1>
          <p className="text-[#4B5563] text-sm">Find the right expert for any challenge</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { resetForm(); setIsCreateOpen(true); }} data-testid="add-expertise-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Expertise
        </Button>
      </div>

      {/* Search */}
      <Card className="border-[#E5E7EB] mb-6">
        <CardContent className="p-4">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              placeholder="Search by skills, keywords, or qualifications..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="expertise-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Expertise Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : expertiseList.length === 0 ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-12 text-center text-[#9CA3AF]">
            <UserSearch className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
            <p>No expertise entries found. Add your first expert.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {expertiseList.map((exp, idx) => (
            <Card key={exp.expertise_id} className="border-[#E5E7EB] card-hover" data-testid={`expertise-${exp.expertise_id}`}>
              <CardContent className="p-6">
                {/* User Info */}
                <div className="flex items-center gap-4 mb-4">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={exp.user?.picture || avatars[idx % avatars.length]} />
                    <AvatarFallback className="bg-[#002FA7] text-white">
                      {getInitials(exp.user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">{exp.user?.name || 'Unknown'}</h3>
                    <p className="text-sm text-[#4B5563]">{exp.department || 'No department'}</p>
                    <p className="text-xs text-[#9CA3AF]">{exp.experience_years || 0} years experience</p>
                  </div>
                </div>

                {/* Skills */}
                {exp.skills?.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center gap-1 text-xs text-[#9CA3AF] mb-1">
                      <Briefcase className="w-3 h-3" /> Skills
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {exp.skills.map((s, i) => (
                        <Badge key={i} className="bg-[#002FA7]/10 text-[#002FA7] border-none text-xs">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Qualifications */}
                {exp.qualifications?.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center gap-1 text-xs text-[#9CA3AF] mb-1">
                      <Award className="w-3 h-3" /> Qualifications
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {exp.qualifications.map((q, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{q}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Keywords */}
                {exp.keywords?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-1 text-xs text-[#9CA3AF] mb-1">
                      <Tag className="w-3 h-3" /> Keywords
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {exp.keywords.map((k, i) => (
                        <span key={i} className="text-xs text-[#4B5563]">#{k}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-[#E5E7EB]">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(exp)}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-[#FF3B30]" onClick={() => handleDelete(exp.expertise_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent data-testid="create-expertise-dialog">
          <DialogHeader><DialogTitle>Add Expertise Entry</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User *</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Skills (comma-separated)</Label>
              <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="React, Python, Project Management" />
            </div>
            <div>
              <Label>Qualifications (comma-separated)</Label>
              <Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} placeholder="MBA, PMP, AWS Certified" />
            </div>
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="server crash, database, networking" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Years of Experience</Label>
                <Input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Engineering" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} data-testid="save-expertise-btn">Add Expertise</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Expertise</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Skills (comma-separated)</Label>
              <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
            </div>
            <div>
              <Label>Qualifications (comma-separated)</Label>
              <Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} />
            </div>
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Years of Experience</Label>
                <Input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpertiseLocatorPage;
